/**
 * Day Blocks shared helpers.
 *
 * A Day Block partitions a calendar day into 4 buckets — Morning, Afternoon,
 * Evening, and Anytime. Users can either bucket tasks explicitly via the
 * `day_block` field, OR let the app infer a bucket from `due_time` using
 * the user's preferred ranges.
 *
 * All ranges are HH:MM in 24-hour. "anytime" has no range.
 */

import { Sun, Sunrise, Moon, Star } from "lucide-react";

export const DAY_BLOCKS = [
  {
    key: "morning",
    label: "Morning",
    emoji: "🌅",
    icon: Sunrise,
    accent: "#F59E0B",
  },
  {
    key: "afternoon",
    label: "Afternoon",
    emoji: "☀️",
    icon: Sun,
    accent: "#F97316",
  },
  {
    key: "evening",
    label: "Evening",
    emoji: "🌙",
    icon: Moon,
    accent: "#7C3AED",
  },
  {
    key: "anytime",
    label: "Anytime",
    emoji: "⭐",
    icon: Star,
    accent: "#0281FF",
  },
];

export const DEFAULT_DAY_BLOCK_RANGES = {
  morning: { start: "05:00", end: "12:00" },
  afternoon: { start: "12:00", end: "18:00" },
  evening: { start: "18:00", end: "24:00" },
  anytime: null,
};

/**
 * Merge user-saved ranges with defaults so missing keys never crash callers.
 */
export function normalizeRanges(saved) {
  const out = { ...DEFAULT_DAY_BLOCK_RANGES };
  if (!saved) return out;
  for (const key of Object.keys(DEFAULT_DAY_BLOCK_RANGES)) {
    if (key === "anytime") continue;
    const r = saved[key];
    if (r && r.start && r.end) {
      out[key] = { start: r.start, end: r.end };
    }
  }
  return out;
}

/** Convert "HH:MM" to a minute count. Returns null if invalid. */
function timeToMinutes(t) {
  if (!t || typeof t !== "string") return null;
  const parts = t.split(":");
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Given a task's due_time/start_time and the user's ranges, return the block
 * key it falls into ("morning"|"afternoon"|"evening") or null if the time is
 * empty / out-of-range.
 */
export function inferBlockFromTime(time, ranges = DEFAULT_DAY_BLOCK_RANGES) {
  const min = timeToMinutes(time);
  if (min == null) return null;
  const r = normalizeRanges(ranges);
  for (const key of ["morning", "afternoon", "evening"]) {
    const range = r[key];
    if (!range) continue;
    const start = timeToMinutes(range.start);
    let end = timeToMinutes(range.end);
    if (start == null || end == null) continue;
    // "24:00" → end of day
    if (end === 0) end = 24 * 60;
    if (min >= start && min < end) return key;
  }
  return null;
}

/**
 * Resolve the EFFECTIVE block for a task:
 *  - explicit `day_block` always wins
 *  - else infer from `due_time` / `start_time` using `ranges`
 *  - else null (=> hidden from blocks view; user can manually drag-in)
 */
export function resolveTaskBlock(task, ranges) {
  if (task?.day_block) return task.day_block;
  const t = task?.due_time || task?.start_time || null;
  return inferBlockFromTime(t, ranges);
}

export function getBlockMeta(key) {
  return DAY_BLOCKS.find((b) => b.key === key) || DAY_BLOCKS[3];
}
